import React, { useState } from "react";
import {
  Database,
  CloudLightning,
  Shield,
  Zap,
  ArrowRight,
  RefreshCw,
  Layers,
  Target,
  Shuffle,
  Maximize,
} from "lucide-react";
import logo from "../../../assets/falcon.png";
import Header from "./Header";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../../utils/auth-config";

export default function Falcon360() {
  const { instance } = useMsal();
  const [activeFeature, setActiveFeature] = useState(null);
  const [activeBenefit, setActiveBenefit] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((e) => {
      console.error(e);
    });
  };

  const keyFeatures = [
    {
      icon: <CloudLightning className="text-blue-400" size={32} />,
      title: "Automated Data Mapping & Transformation",
      description:
        "Intelligently aligns data to fit target schema, saving time and reducing human error",
    },
    {
      icon: <Layers className="text-purple-400" size={32} />,
      title: "Multi-Platform Compatibility",
      description: "Seamless migrations across SAP, Oracle, Bullhorn, and more",
    },
    {
      icon: <Shield className="text-green-400" size={32} />,
      title: "Data Integrity and Security",
      description:
        "Strict controls and validation checks to protect sensitive business information",
    },
    {
      icon: <RefreshCw className="text-teal-400" size={32} />,
      title: "Comprehensive Migration Management",
      description: "Full spectrum support from data extraction to validation",
    },
  ];

  const keyBenefits = [
    {
      icon: <Zap className="text-yellow-400" size={32} />,
      title: "Efficiency",
      description:
        "Streamlines the data migration process, reducing the complexity of managing multiple systems",
    },
    {
      icon: <Target className="text-red-400" size={32} />,
      title: "Precision",
      description:
        "Ensures accurate data mapping and transformation to maintain consistency",
    },
    {
      icon: <Shuffle className="text-indigo-400" size={32} />,
      title: "Flexibility",
      description:
        "Suitable for migrations across various systems, perfect for diverse business needs",
    },
    {
      icon: <Maximize className="text-emerald-400" size={32} />,
      title: "Scalability",
      description:
        "Adaptable for migrations from single datasets to entire enterprise systems",
    },
  ];

  return (
    <div className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-indigo-900 min-h-screen text-white overflow-hidden">
      <Header />
      <div
        className="relative bg-gradient-to-b from-transparent via-blue-900 to-blue-900 min-h-screen text-white overflow-hidden"
        style={{ backgroundColor: "rgba(58, 89, 209, 0.8)" }}
      >
        {/* Animated Background Particles */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          {Array.from({ length: 100 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-blue-500"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 5 + 2}px`,
                height: `${Math.random() * 5 + 2}px`,
                opacity: Math.random() * 0.5,
                animation: `float ${Math.random() * 20 + 10}s linear infinite`,
                animationDelay: `${Math.random() * 10}s`,
              }}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto px-4 py-20 relative">
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-block relative mb-6">
              <div className="center">
                <img
                  src={logo}
                  alt="Falcon Logo"
                  className="w-[250px] h-[250px] object-contain"
                />
              </div>
            </div>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Seamless Data Migrations with BlueGecko's GenAI Engine
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="flex justify-center mb-12">
            {[
              { key: "overview", label: "Overview" },
              { key: "features", label: "Key Features" },
              { key: "benefits", label: "Benefits" },
            ].map((tab) => (
              <button
                key={tab.key}
                className={`px-6 py-3 mx-2 rounded-lg transition-all duration-300
                  ${
                    activeSection === tab.key
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                      : "bg-gray-800 text-blue-200 hover:bg-gray-700"
                  }`}
                onClick={() => setActiveSection(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Sections */}
          {activeSection === "overview" && (
            <div className="bg-gray-800 bg-opacity-50 backdrop-blur-lg rounded-2xl p-8 border border-blue-500 border-opacity-30">
              <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
                Accelerate Data Understanding for Your Teams
              </h2>
              <p className="text-blue-200 mb-4">
                BlueGecko understands that data preparation can be
                time-consuming for Data Stewards, Data Owners, System
                Architects, Business Controllers, and Data Analysts. Our GenAI
                Engine is designed to make your data operations smoother and
                more efficient.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <Zap className="text-blue-400 mr-3" size={24} />
                  <span className="text-blue-200">
                    Automated Data Analysis: Uncover patterns and insights
                    across large volumes of legacy data
                  </span>
                </div>
                <div className="flex items-center">
                  <Database className="text-purple-400 mr-3" size={24} />
                  <span className="text-blue-200">
                    Intelligent Data Categorization: Automate classification
                    based on content, context, and metadata
                  </span>
                </div>
                <div className="flex items-center">
                  <Shield className="text-green-400 mr-3" size={24} />
                  <span className="text-blue-200">
                    Enhanced Data Documentation: Generate comprehensive data
                    dictionaries and lineage guidelines
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeSection === "features" && (
            <div className="grid md:grid-cols-2 gap-6">
              {keyFeatures.map((feature, index) => (
                <div
                  key={index}
                  className={`p-6 rounded-2xl transition-all duration-300 
                    ${
                      activeFeature === index
                        ? "bg-gray-700 border-2 border-blue-500 shadow-2xl"
                        : "bg-gray-800 bg-opacity-50 border border-transparent"
                    }`}
                  onMouseEnter={() => setActiveFeature(index)}
                  onMouseLeave={() => setActiveFeature(null)}
                >
                  <div className="flex items-center mb-4">
                    {feature.icon}
                    <h3 className="ml-4 text-xl font-semibold text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-blue-200">{feature.description}</p>
                </div>
              ))}
            </div>
          )}

          {activeSection === "benefits" && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent text-center">
                Why Choose Falcon Mapping?
              </h2>
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {[
                  {
                    title: "Fast and Reliable",
                    description:
                      "Reduce migration timelines with optimized processes, ensuring faster execution with minimal business disruptions.",
                  },
                  {
                    title: "Scalable for All Needs",
                    description:
                      "Designed to handle migrations from small-scale transfers to enterprise-level data migrations.",
                  },
                  {
                    title: "End-to-End Support",
                    description:
                      "Comprehensive support from initial planning to post-migration analysis, ensuring a smooth transition.",
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-800 bg-opacity-50 border border-transparent p-6 rounded-xl"
                  >
                    <h3 className="text-xl font-semibold text-blue-300 mb-4">
                      {item.title}
                    </h3>
                    <p className="text-blue-200">{item.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent text-center">
                  Benefits
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {keyBenefits.map((benefit, index) => (
                    <div
                      key={index}
                      className={`p-6 rounded-2xl transition-all duration-300 
                        ${
                          activeBenefit === index
                            ? "bg-gray-700 border-2 border-blue-500 shadow-2xl"
                            : "bg-gray-800 bg-opacity-50 border border-transparent"
                        }`}
                      onMouseEnter={() => setActiveBenefit(index)}
                      onMouseLeave={() => setActiveBenefit(null)}
                    >
                      <div className="flex items-center mb-4">
                        {benefit.icon}
                        <h4 className="ml-4 text-xl font-semibold text-white">
                          {benefit.title}
                        </h4>
                      </div>
                      <p className="text-blue-200">{benefit.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
              Start Your Migration Journey Today
            </h3>
            <p className="text-blue-200 max-w-2xl mx-auto mb-8">
              Ready to simplify your data migrations with Falcon Mapping? Let
              BlueGecko guide you through the process with powerful tools and a
              seamless migration experience.
            </p>
            <button
              onClick={handleLogin}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 flex items-center justify-center mx-auto group"
            >
              Contact Us & Request a Demo
              <ArrowRight
                className="ml-3 group-hover:translate-x-2 transition-transform"
                size={20}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
